'use client';

import { ListTodo } from 'lucide-react';
import Chip from '~/components/Chip';
import Skeleton from '~/components/Loading/Skeleton';
import PendingLink from '~/components/Navigation/PendingLink';
import { SettingsEmptyState } from '~/components/settings/SettingsEmptyState';
import { buttonVariants } from '~/components/ui/button';
import { Routes } from '~/config';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import { cn } from '~/lib/utils';
import { useHomeQueueOverview } from './useHomeQueueOverview';

type HomeReviewQueueProps = {
  enabled: boolean;
  hideHeader?: boolean;
  /** Open-count chip. Off when the parent tab already shows the same total. */
  showCount?: boolean;
};

function ReviewQueueSkeleton() {
  return (
    <div className="border-border bg-card overflow-hidden rounded-xl border" aria-hidden>
      <table className="w-full table-fixed border-collapse text-left">
        <colgroup>
          <col />
          <col className="w-[72px]" />
        </colgroup>
        <thead>
          <tr className="text-muted-foreground text-[13px]">
            <th className="px-4 pt-2.5 pb-2 font-medium">Queue</th>
            <th className="px-4 pt-2.5 pb-2 text-right font-medium">Open</th>
          </tr>
        </thead>
        <tbody>
          {[...Array(4)].map((_, i) => (
            <tr key={i} className="border-border/50 border-t">
              <td className="px-4 py-2.5">
                <Skeleton className="h-4 w-32" />
              </td>
              <td className="px-4 py-2.5">
                <Skeleton className="ml-auto h-4 w-8" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function HomeReviewQueue({
  enabled,
  hideHeader,
  showCount = true,
}: HomeReviewQueueProps) {
  const { workspaceId } = useWorkspace();
  const { data, isLoading } = useHomeQueueOverview(enabled);

  if (!enabled) return null;

  const queues = data?.queues ?? [];
  const hasQueues = queues.length > 0;
  const openCount = queues.reduce((n, queue) => n + queue.openCount, 0);
  const ws = (path: string) => `/${workspaceId}${path}`;
  const settingsHref = ws(`${Routes.SETTINGS}/review-queues`);

  const body = isLoading ? (
    <ReviewQueueSkeleton />
  ) : !hasQueues ? (
        <SettingsEmptyState
          size="section"
          icon={ListTodo}
          title="No Review Queues Yet"
          description="Connect a project to route marketing reviews here."
          action={
            <PendingLink
              href={settingsHref}
              className={cn(buttonVariants({ size: 'sm', variant: 'default' }), 'normal-case')}
            >
              Open Review Queues settings
            </PendingLink>
          }
          className="min-h-0 py-4"
        />
      ) : (
        <div className="border-border bg-card overflow-hidden rounded-xl border">
          <table className="w-full table-fixed border-collapse text-left">
            <colgroup>
              <col />
              <col className="w-[72px]" />
            </colgroup>
            <thead>
              <tr className="text-muted-foreground text-[13px]">
                <th className="px-4 pt-2.5 pb-2 font-medium">Queue</th>
                <th className="px-4 pt-2.5 pb-2 text-right font-medium">Open</th>
              </tr>
            </thead>
            <tbody>
              {queues.map((queue) => (
                <tr
                  key={queue.id}
                  className="border-border/50 hover:bg-muted relative border-t transition-colors"
                >
                  <td className="overflow-hidden px-4 py-2.5 align-middle">
                    <PendingLink
                      href={ws(`${Routes.PROJECTS}/${queue.id}/list`)}
                      className="text-foreground focus-visible:ring-ring/50 after:absolute after:inset-0 block truncate text-sm font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-inset"
                    >
                      {queue.name}
                    </PendingLink>
                  </td>
                  <td className="px-4 py-2.5 align-middle">
                    <span
                      className={cn(
                        'block text-right text-sm tabular-nums',
                        queue.openCount > 0
                          ? 'text-foreground font-medium'
                          : 'text-muted-foreground',
                      )}
                    >
                      {queue.openCount}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

  if (hideHeader) return body;

  return (
    <section aria-labelledby="home-review-queue-heading">
      <div className={cn('mb-2 flex gap-2', showCount ? 'items-center' : 'items-baseline')}>
        <h2
          id="home-review-queue-heading"
          className="text-foreground text-sm font-semibold tracking-tight"
        >
          Review Queues
        </h2>
        {showCount ? (
          isLoading ? (
            <Skeleton className="h-5 min-w-[1.375rem] rounded-md" />
          ) : (
            <Chip
              level="neutral"
              text={String(openCount)}
              className="h-5 min-w-[1.375rem] justify-center px-1.5 tabular-nums"
            />
          )
        ) : null}
      </div>
      {body}
    </section>
  );
}
